import { useEffect, useState } from 'react'

import type { GoalSportEventState, GoalSportPhase } from '@/services/jsonDataService'

/**
 * Sum the first `count` end-elapsed values from the array, treating null/missing
 * entries as 0. Used to derive the locked elapsed time at the start of period n
 * (or the displayed time when paused/finished).
 */
const sumCompletedPeriods = (endElapsed: (number | null)[] | undefined, count: number): number => {
  if (!endElapsed || count <= 0) return 0
  let total = 0
  for (let i = 0; i < count && i < endElapsed.length; i++) {
    const v = endElapsed[i]
    if (typeof v === 'number' && !Number.isNaN(v)) total += v
  }
  return total
}

/**
 * Derive the displayed elapsed seconds for the GoalSport scorebug.
 *
 * - PRE: 0
 * - RUNNING period n: sum of completed periods + (now - periodStartedAt)
 * - BREAK after period n: sum of periods 0..n-1 (frozen)
 * - END: sum of all completed periods (frozen)
 *
 * Always computed from `Date.now()` against `periodStartedAt`, never an
 * accumulator — this avoids drift when the tab is backgrounded and timers
 * are throttled.
 */
export const deriveElapsedSeconds = (eventState: GoalSportEventState | undefined, now: number = Date.now()): number => {
  if (!eventState) return 0

  const phase: GoalSportPhase = eventState.phase ?? 'PRE'
  const period = Math.max(1, eventState.period ?? 1)
  const endElapsed = eventState.periodEndElapsedSeconds

  if (phase === 'PRE') return 0

  if (phase === 'RUNNING') {
    const completed = sumCompletedPeriods(endElapsed, period - 1)
    if (!eventState.periodStartedAt) return completed
    const startedAt = new Date(eventState.periodStartedAt).getTime()
    if (Number.isNaN(startedAt)) return completed
    const live = Math.max(0, (now - startedAt) / 1000)
    return completed + live
  }

  if (phase === 'BREAK') {
    return sumCompletedPeriods(endElapsed, period)
  }

  if (phase === 'END') {
    const periodCount = Math.max(period, eventState.periodCount ?? period)
    return sumCompletedPeriods(endElapsed, periodCount)
  }

  return 0
}

/**
 * Format seconds as `MM:SS` with no upper cap (juniors play shorter, seniors
 * play 45+ with stoppage). Negative or NaN values render as `00:00`.
 */
export const formatClock = (seconds: number): string => {
  if (!Number.isFinite(seconds) || seconds < 0) return '00:00'
  const total = Math.floor(seconds)
  const mm = Math.floor(total / 60)
  const ss = total % 60
  return `${String(mm).padStart(2, '0')}:${String(ss).padStart(2, '0')}`
}

/**
 * React hook that triggers a re-render every `intervalMs` (default 250ms)
 * while `enabled` is true. The actual elapsed time is always recomputed from
 * `Date.now()` by callers — this hook only nudges React to redraw.
 *
 * 250ms is a balance between smooth UI and CPU; the displayed clock only
 * has 1-second resolution so we just need to re-render at least that often.
 */
export const useTickingNow = (enabled: boolean, intervalMs = 250): number => {
  const [now, setNow] = useState(() => Date.now())

  useEffect(() => {
    if (!enabled) return
    const id = window.setInterval(() => setNow(Date.now()), intervalMs)
    return () => window.clearInterval(id)
  }, [enabled, intervalMs])

  return now
}

/**
 * Resolve a period label (e.g. "1H", "2H") from sport config, with sensible
 * fallbacks if config is missing or the period exceeds the configured length.
 */
export const resolvePeriodLabel = (period: number, periodLabels: string[] | undefined): string => {
  const idx = Math.max(0, period - 1)
  if (periodLabels && periodLabels[idx]) return periodLabels[idx]
  return `P${period}`
}

/**
 * Resolve a break label after a given period (e.g. "Half Time" after period 1).
 */
export const resolveBreakLabel = (period: number, breakLabels: string[] | undefined): string => {
  const idx = Math.max(0, period - 1)
  if (breakLabels && breakLabels[idx]) return breakLabels[idx]
  return 'Break'
}
